import React from "react";

import { useDatasetMappedValue } from "hooks/useDatasetMappedValue";
import { DatasetDataInfo } from "models/dataset-models";
import { formatDuration, formatTime, getDiffSeconds } from "utils/time-utils";
import { formatDatasetValue } from "utils/value-format-utils";
import { InstantDoubleValue } from "api/model/dataset/InstantDoubleValue";
import T from "services/lang/LanguageService";

type DatasetTooltipProps = {
  data: InstantDoubleValue | null | undefined;
  time: Date;
  dataset: DatasetDataInfo;
  hide?: boolean;
};

function DatasetTooltipText({ data, time, dataset, hide = false }: DatasetTooltipProps) {
  if (hide) {
    return null;
  }
  const fmtTime = formatTime(time);
  const fmtLoadTime = formatTime(dataset?.loadedTime);
  const noData = !data!;

  let tagValueAge;
  if (+dataset?.loadedTime! > +time!) {
    tagValueAge = <>({T("word.age")}: {formatDuration(getDiffSeconds(dataset?.loadedTime!, time!))} )</>;
  } else {
    tagValueAge = <>({T("word.age")}: - {formatDuration(getDiffSeconds(time!, dataset?.loadedTime!))} )</>;
  }

  return (
    <div style={{ whiteSpace: "pre" }}>
      <div>
        <b>{dataset?.title}:</b>{" "}
        {dataset.stringValueMapper ? (
          <MappedValue mapValue={data?.value} mapperId={dataset.stringValueMapper} />
        ) : (
          <i>
            {formatDatasetValue(dataset, data)}
            {data?.value && ` ${  dataset?.unit}`}
          </i>
        )}
      </div>
      <div>
        <b>{T("word.mesurement-date")}:</b> <i>{fmtTime || T("word.no-data")}</i>
      </div>
      <div>
        <b>{T("word.last-checked")}:</b>{" "}
        <i>
          {fmtLoadTime} {!noData ? tagValueAge : ""}
        </i>
      </div>
    </div>
  );
}

export default DatasetTooltipText;

type MappedValueProps = {
  mapValue: number | undefined;
  mapperId: number;
};

export const MappedValue: React.FC<MappedValueProps> = ({ mapValue, mapperId }) => {
  const mappedValue = useDatasetMappedValue(mapValue, mapperId);

  return (
    <i>
      {mappedValue ? `${mapValue} - ${mappedValue}` : <span className="pi pi-spin pi-spinner" />}
    </i>
  );
};
